import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/auth';
import { usePermissions } from '@/shared/hooks/usePermissions';
import type { Permission } from '@/shared/types/permissions';
import type { MemberRole } from '@/shared/types/member';
import { Spinner } from './ui/Spinner/Spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;

  /** Require specific permission to access this route */
  permission?: Permission;

  /** Require any of these permissions to access this route */
  anyPermission?: Permission[];

  /** Require specific role to access this route */
  role?: MemberRole;

  /** Require any of these roles to access this route */
  anyRole?: MemberRole[];

  /** Redirect path when permission denied (default: /dashboard) */
  redirectTo?: string;
}

/**
 * Route wrapper that requires authentication and optionally specific permissions.
 * Redirects to login if not authenticated, or to redirectTo if permission denied.
 *
 * IMPORTANT: This is a UX enhancement. Backend enforces all permissions.
 *
 * @example
 * // Basic auth check
 * <ProtectedRoute>
 *   <Layout />
 * </ProtectedRoute>
 *
 * // With permission requirement
 * <ProtectedRoute permission="manage_billing" redirectTo="/dashboard">
 *   <BillingPage />
 * </ProtectedRoute>
 *
 * // With role requirement
 * <ProtectedRoute role="owner" redirectTo="/dashboard">
 *   <OwnerOnlyPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  permission,
  anyPermission,
  role: requiredRole,
  anyRole,
  redirectTo = '/dashboard',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, initialize } = useAuthStore();
  const { can, canAny, role } = usePermissions();
  const location = useLocation();

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" className="text-blue-600" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission requirements
  let hasAccess = true;

  if (permission) {
    hasAccess = hasAccess && can(permission);
  }

  if (anyPermission && anyPermission.length > 0) {
    hasAccess = hasAccess && canAny(anyPermission);
  }

  if (requiredRole) {
    hasAccess = hasAccess && role === requiredRole;
  }

  if (anyRole && anyRole.length > 0) {
    hasAccess = hasAccess && role !== null && anyRole.includes(role);
  }

  // Permission denied - redirect to fallback
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
